import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pencil, Trash2, Eye, Printer, FileText, DollarSign, Search, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import FashionDesignForm from './FashionDesignForm';
import { usePermissions } from '@/hooks/use-permissions';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';
import html2pdf from 'html2pdf.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'INSPIRATION', label: 'Inspiration' },
  { value: 'SKETCHING', label: 'Sketching' },
  { value: 'SKETCHED', label: 'Sketched' },
  { value: 'SOURCING', label: 'Sourcing' },
  { value: 'EXT_PRODUCTION', label: 'Ext Production' },
  { value: 'SAMPLING', label: 'Sampling' },
  { value: 'FINISHED_GOOD', label: 'Finished Good' },
  { value: 'ALTERATION', label: 'Alteration' },
  { value: 'ALTERED', label: 'Altered' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED_INSPIRATION', label: 'Rejected Inspiration' },
  { value: 'REJECTED_POST_SAMPLING', label: 'Rejected Post Sampling' },
  { value: 'REJECTED_SOURCING', label: 'Rejected Sourcing' }
];

export default function FashionDesign({ user, onLogout }) {
  const navigate = useNavigate();
  const { hasPermission, canCreate, canEdit, canDelete, canApprove } = usePermissions(user);
  const [designs, setDesigns] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewDetails, setViewDetails] = useState(null);
  const [editingDesign, setEditingDesign] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [designToDelete, setDesignToDelete] = useState(null);
  const [showTechPackPdf, setShowTechPackPdf] = useState(false);
  const [showCostingDialog, setShowCostingDialog] = useState(false);
  const [samplingRoundDialogOpen, setSamplingRoundDialogOpen] = useState(false);
  const [samplingRoundNumber, setSamplingRoundNumber] = useState('');
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [samplingRounds, setSamplingRounds] = useState([]);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [datasetSelectionOpen, setDatasetSelectionOpen] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportOption, setReportOption] = useState('all'); // 'all', 'specific'
  const [specificRoundNumbers, setSpecificRoundNumbers] = useState('');
  const [samplingTimeData, setSamplingTimeData] = useState(null);
  
  // Authorization for Tech Pack and Costing
  const [techPackAuthDialogOpen, setTechPackAuthDialogOpen] = useState(false);
  const [costingAuthDialogOpen, setCostingAuthDialogOpen] = useState(false);
  
  // LN-X Reports
  const [showLnxReportPdf, setShowLnxReportPdf] = useState(false);
  const [lnxReportData, setLnxReportData] = useState(null);

  // Search and filter states
  const [mainSearchTerm, setMainSearchTerm] = useState('');
  const [articleCodes, setArticleCodes] = useState([]);
  const [fabricCodes, setFabricCodes] = useState([]);
  const [trimCodes, setTrimCodes] = useState([]);
  const [digitalDesignCodes, setDigitalDesignCodes] = useState([]);
  
  const [filterArticle, setFilterArticle] = useState('');
  const [filterFabric, setFilterFabric] = useState('');
  const [filterTrim, setFilterTrim] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDigitalDesign, setFilterDigitalDesign] = useState('');
  
  // Master data popup states
  const [masterDataOpen, setMasterDataOpen] = useState(false);
  const [masterDataDetails, setMasterDataDetails] = useState(null);
  const [masterDataLoading, setMasterDataLoading] = useState(false);
  const [masterDataCode, setMasterDataCode] = useState('');
  const [zoomImage, setZoomImage] = useState(null);
  const [zoomImageOpen, setZoomImageOpen] = useState(false);
  const pdfContentRef = useRef(null);

  // Barcode scanning
  const handleBarcodeSearch = (e) => {
    if (e && e.key === 'Enter' && mainSearchTerm.trim() !== '') {
      e.preventDefault();
      const query = mainSearchTerm.toLowerCase().trim();
      
      console.log('🔍 Searching for design:', query);
      
      // Find design by article code (LN-X code)
      const found = designs.find(design => {
        const code = design.article_code?.toLowerCase();
        // Exact match
        if (code === query) {
          return true;
        }
        // Partial match
        if (code?.includes(query)) {
          return true;
        }
        return false;
      });
      
      if (found) {
        console.log('✅ Found design:', found.article_code);
        setViewDetails(found);
        setViewOpen(true);
        toast.success(`✅ Design found: ${found.article_code}`);
      } else {
        toast.error(`❌ No design found with code: ${query}`);
      }
    }
  };

  useEffect(() => {
    fetchDesigns();
    fetchFilterOptions();
    fetchSamplingRounds();
  }, []);

  // Enrich design data with live master data
  const enrichWithLiveData = async (design) => {
    try {
      const token = localStorage.getItem('token');
      const enrichedDesign = { ...design };

      // Fetch live fabric data
      if (design.fabrics && design.fabrics.length > 0) {
        const fabricsResponse = await axios.get(`${API}/fabric-master`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        enrichedDesign.fabrics = design.fabrics.map(fabric => {
          const masterFabric = fabricsResponse.data.find(f => f.ln_code === fabric.fabric_code);
          if (masterFabric) {
            const masterColor = masterFabric.colors?.find(c => c.color_code === fabric.color_code);
            if (masterColor) {
              return {
                ...fabric,
                color_name: masterColor.color_name,
                width: masterFabric.width,
                gsm: masterFabric.gsm,
                composition: masterFabric.composition,
                fabric_type: masterFabric.fabric_type,
                price_per_meter: masterColor.price_per_meter
              };
            }
          }
          return fabric;
        });
      }

      // Fetch live trim data
      if (design.trims && design.trims.length > 0) {
        const trimsResponse = await axios.get(`${API}/trims-master`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const packagingResponse = await axios.get(`${API}/packaging-master`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        enrichedDesign.trims = design.trims.map(trim => {
          // Check trims
          let masterTrim = trimsResponse.data.find(t => t.ln_z_code === trim.trim_code);
          if (!masterTrim) {
            // Check packaging
            masterTrim = packagingResponse.data.find(p => p.ln_y_code === trim.trim_code);
          }
          
          if (masterTrim) {
            const masterColor = masterTrim.colors?.find(c => c.color_code === trim.color_code);
            if (masterColor) {
              return {
                ...trim,
                color_name: masterColor.color_name,
                price: masterColor.price
              };
            }
          }
          return trim;
        });
      }

      return enrichedDesign;
    } catch (error) {
      console.error('Error enriching with live data:', error);
      return design; // Return original if enrichment fails
    }
  };

  const fetchDesigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/designs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Enrich ALL designs with live data for grid display
      const enrichedDesigns = await Promise.all(response.data.map(design => enrichWithLiveData(design)));
      setDesigns(enrichedDesigns);
    } catch (error) {
      console.error('Failed to fetch designs:', error);
      toast.error('Failed to fetch designs');
    }
  };

  // Auto-refresh every 5 seconds, but pause when any dialog is open
  const isUserEditing = deleteDialogOpen || authDialogOpen || samplingRoundDialogOpen || 
                        reportDialogOpen || techPackAuthDialogOpen || costingAuthDialogOpen || 
                        viewOpen || datasetSelectionOpen || masterDataOpen || 
                        showCostingDialog || showTechPackPdf || showLnxReportPdf;
  useAutoRefresh(fetchDesigns, 5, isUserEditing);

  const fetchSamplingRounds = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/designs/sampling-rounds`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSamplingRounds(response.data);
    } catch (error) {
      console.error('Failed to fetch sampling rounds:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch articles
      const articlesResponse = await axios.get(`${API}/articles-master`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticleCodes(articlesResponse.data.map(a => ({ 
        value: a.ln_x_code, 
        label: a.ln_x_code 
      })));

      // Fetch fabrics
      const fabricsResponse = await axios.get(`${API}/fabrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFabricCodes(fabricsResponse.data.map(f => ({ 
        value: f.ln_code, 
        label: f.ln_code 
      })));

      // Fetch trims
      const trimsResponse = await axios.get(`${API}/trims`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrimCodes(trimsResponse.data.map(t => ({ 
        value: t.ln_z_code, 
        label: t.ln_z_code 
      })));

      // Fetch digital designs
      const designsResponse = await axios.get(`${API}/digital-designs-master`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDigitalDesignCodes(designsResponse.data.map(d => ({ 
        value: d.ln_t_code, 
        label: d.ln_t_code 
      })));
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const handleOpenForm = () => {
    setEditingDesign(null);
    setFormOpen(true);
  };

  const handleEdit = (design) => {
    setEditingDesign(design);
    setFormOpen(true);
  };

  const handleFormSuccess = () => {
    fetchDesigns();
  };

  // Enrich design data with live master data
  // Fetch and show master data popup
  const handleMasterDataClick = async (code, type) => {
    setMasterDataLoading(true);
    setMasterDataOpen(true);
    setMasterDataCode(code);
    
    // Map frontend type to backend expected format
    const typeMap = {
      'Fabric': 'FABRIC LN',
      'Trim': 'TRIMS AND ACCESSORIES LN-Z',
      'Trims': 'TRIMS AND ACCESSORIES LN-Z',  // Handle both singular and plural
      'Packaging': 'PACKAGING LN-Y',
      'DigitalDesign': 'DIGITAL DESIGN LN-T'
    };
    
    const backendType = typeMap[type] || type;
    
    try {
      const token = localStorage.getItem('token');
      // Add timestamp to prevent browser caching and ensure live data fetch
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API}/master-data/by-code/${backendType}/${code}?_t=${timestamp}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      setMasterDataDetails(response.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
      toast.error('Failed to fetch master data details');
      setMasterDataDetails(null);
    } finally {
      setMasterDataLoading(false);
    }
  };

  const handleView = async (design) => {
    console.log('🔍 View Design Data:', design);
    console.log('📦 Article Barcode:', design.article_barcode);
    
    // Enrich with live data from master
    const enrichedDesign = await enrichWithLiveData(design);
    setViewDetails(enrichedDesign);
    setViewOpen(true);
    
    // Fetch sampling time data for this article
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sampling-time-tracker/by-article/${design.article_code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSamplingTimeData(response.data);
    } catch (error) {
      console.error('Error fetching sampling time data:', error);
      setSamplingTimeData(null);
    }
    
    // Fetch LN-X report for this design (only if status is FINISHED_GOOD)
    if (design.status === 'FINISHED_GOOD' || design.status === 'APPROVED') {
      try {
        const token = localStorage.getItem('token');
        const lnxReports = await axios.get(`${API}/lnx-reports`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const report = lnxReports.data.find(r => r.design_id === design.id);
        setLnxReportData(report || null);
      } catch (error) {
        console.error('Error fetching LN-X report:', error);
        setLnxReportData(null);
      }
    } else {
      setLnxReportData(null);
    }
  };

  const handleDeleteClick = (design) => {
    setDesignToDelete(design);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/designs/${designToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Design deleted successfully');
      setDeleteDialogOpen(false);
      setDesignToDelete(null);
      fetchDesigns();
    } catch (error) {
      console.error('Error deleting design:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete design');
      setDeleteDialogOpen(false);
    }
  };

  const handleStatusChange = async (designId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API}/designs/${designId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Design moved to ${newStatus.replace(/_/g, ' ')}`);
      
      // Update local state
      setDesigns(designs.map(d => d.id === designId ? response.data : d));
      
      // Update view details if open
      if (viewDetails && viewDetails.id === designId) {
        setViewDetails(response.data);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  // Calculate status counts for current dataset only
  const statusCounts = STATUS_OPTIONS.reduce((acc, status) => {
    acc[status.value] = designs.filter(d => d.status === status.value && d.project_cycle === 0).length;
    return acc;
  }, {});

  const totalCount = Object.values(statusCounts).reduce((sum, count) => sum + count, 0);

  // Generate Report
  const generateReportData = (option, roundNumbers = []) => {
    let datasetToAnalyze = [];
    let datasetLabel = '';
    
    if (option === 'all') {
      datasetToAnalyze = designs;
      datasetLabel = 'All Articles';
    } else if (option === 'specific' && roundNumbers.length > 0) {
      // Filter designs that belong to the specified sampling rounds
      datasetToAnalyze = designs.filter(d => {
        // Find the sampling round this design belongs to
        const round = samplingRounds.find(sr => sr.article_ids.includes(d.id));
        return round && roundNumbers.includes(round.round_number);
      });
      datasetLabel = `Dataset Round(s): ${roundNumbers.join(', ')}`;
    }
    
    // Status counts
    const statusData = STATUS_OPTIONS.map(status => ({
      status: status.label,
      count: datasetToAnalyze.filter(d => d.status === status.value).length
    })).filter(item => item.count > 0);

    const total = datasetToAnalyze.length;

    // Category counts
    const categoryData = {};
    datasetToAnalyze.forEach(design => {
      const cat = design.category || 'Uncategorized';
      categoryData[cat] = (categoryData[cat] || 0) + 1;
    });

    // Analysis: Which categories get approved/rejected most
    const approvedByCategory = {};
    const rejectedByCategory = {};
    
    datasetToAnalyze.forEach(design => {
      const cat = design.category || 'Uncategorized';
      if (design.status === 'APPROVED') {
        approvedByCategory[cat] = (approvedByCategory[cat] || 0) + 1;
      }
      if (design.status === 'REJECTED_INSPIRATION' || design.status === 'REJECTED_POST_SAMPLING' || design.status === 'REJECTED_SOURCING') {
        rejectedByCategory[cat] = (rejectedByCategory[cat] || 0) + 1;
      }
    });

    const mostApprovedCategory = Object.entries(approvedByCategory).sort((a, b) => b[1] - a[1])[0];
    const mostRejectedCategory = Object.entries(rejectedByCategory).sort((a, b) => b[1] - a[1])[0];

    return {
      datasetLabel,
      total,
      statusData,
      categoryData,
      mostApprovedCategory,
      mostRejectedCategory,
      generatedAt: new Date().toLocaleString()
    };
  };

  const handleGenerateReport = () => {
    setReportOption('all');
    setSpecificRoundNumbers('');
    setDatasetSelectionOpen(true);
  };

  const handleDatasetSelection = () => {
    let roundNumbers = [];
    
    if (reportOption === 'specific') {
      // Parse the comma-separated round numbers
      const parsed = specificRoundNumbers.split(',').map(num => parseInt(num.trim())).filter(num => !isNaN(num));
      if (parsed.length === 0) {
        toast.error('Please enter valid dataset round numbers');
        return;
      }
      roundNumbers = parsed;
    }
    
    const data = generateReportData(reportOption, roundNumbers);
    
    if (data.total === 0) {
      toast.error('No articles found for the selected dataset');
      return;
    }
    
    setReportData(data);
    setDatasetSelectionOpen(false);
    setReportDialogOpen(true);
  };

  // Handle New Dataset
  const handleNewDataset = () => {
    if (designs.length === 0) {
      toast.error('No articles available to create a sampling round');
      return;
    }
    setSelectedArticles([]); // Start with empty selection
    setSamplingRoundDialogOpen(true);
  };

  // Handle Tech Pack button click - requires authorization
  const handleTechPackClick = () => {
    setTechPackAuthDialogOpen(true);
  };

  // Handle Costing button click - requires authorization
  const handleCostingClick = () => {
    setCostingAuthDialogOpen(true);
  };

  // Handle LN-X Report button click - no authorization required
  const handleLnxReportClick = async () => {
    if (!lnxReportData) return;
    setShowLnxReportPdf(true);
  };

  // Handle Tech Pack authorization success
  const handleTechPackAuthSuccess = () => {
    setTechPackAuthDialogOpen(false);
    setShowTechPackPdf(true);
  };

  // Handle Costing authorization success
  const handleCostingAuthSuccess = () => {
    setCostingAuthDialogOpen(false);
    setShowCostingDialog(true);
  };

  const handleCreateSamplingRound = async () => {
    if (!samplingRoundNumber || selectedArticles.length === 0) {
      toast.error('Please enter round number and select articles');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/designs/sampling-rounds`,
        {
          round_number: parseInt(samplingRoundNumber),
          article_ids: selectedArticles
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Sampling Round ${samplingRoundNumber} created with ${selectedArticles.length} articles`);
      setSamplingRoundDialogOpen(false);
      setSamplingRoundNumber('');
      setSelectedArticles([]);
      fetchDesigns();
      fetchSamplingRounds();
    } catch (error) {
      console.error('Error creating sampling round:', error);
      toast.error(error.response?.data?.detail || 'Failed to create sampling round');
    }
  };

  // Download Design as PDF
  const handleDownloadPDF = async () => {
    if (!viewDetails || !pdfContentRef.current) return;
    
    try {
      const element = pdfContentRef.current;
      
      // Hide elements with no-pdf class
      const noPdfElements = element.querySelectorAll('.no-pdf');
      noPdfElements.forEach(el => el.style.display = 'none');
      
      const opt = {
        margin: 10,
        filename: `${viewDetails.article_code}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      
      await html2pdf().set(opt).from(element).save();
      
      // Restore hidden elements
      noPdfElements.forEach(el => el.style.display = '');
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
      
      // Restore hidden elements in case of error
      const noPdfElements = pdfContentRef.current.querySelectorAll('.no-pdf');
      noPdfElements.forEach(el => el.style.display = '');
    }
  };

  // Filter designs
  const filteredDesigns = designs.filter(design => {
    // Main search (LN-X code or barcode)
    const matchesMainSearch = !mainSearchTerm || 
      design.article_code?.toLowerCase().includes(mainSearchTerm.toLowerCase());

    // Filter by article
    const matchesArticle = !filterArticle || design.article_code === filterArticle;

    // Filter by fabric
    const matchesFabric = !filterFabric || 
      design.fabrics?.some(f => f.fabric_code === filterFabric);

    // Filter by trim
    const matchesTrim = !filterTrim || 
      design.trims?.some(t => t.trim_code === filterTrim);

    // Filter by status
    const matchesStatus = !filterStatus || design.status === filterStatus;

    // Filter by digital design
    const matchesDigitalDesign = !filterDigitalDesign || 
      design.digital_design_code === filterDigitalDesign;

    return matchesMainSearch && matchesArticle && matchesFabric && 
           matchesTrim && matchesStatus && matchesDigitalDesign;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/design-studio')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Design HQ
              </h1>
              <p className="text-sm text-gray-600">Manage design workflow from inspiration to approval</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {canCreate('fashion_design') && (
              <Button onClick={handleOpenForm} className="gap-2 bg-black hover:bg-gray-800">
                <Plus className="w-4 h-4" />
                New Design
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6 space-y-4">
          {/* Main Search Bar */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search or scan barcode"
              value={mainSearchTerm}
              onChange={(e) => setMainSearchTerm(e.target.value)}
              onKeyDown={handleBarcodeSearch}
              className="pl-10"
              autoComplete="off"
            />
          </div>

          {/* Filters Section */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Filter by Fabric</Label>
            <SearchableSelect
              options={[{ value: '', label: 'All Fabrics' }, ...fabricCodes]}
              value={filterFabric}
              onChange={setFilterFabric}
              placeholder="Select fabric"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Filter by Trims</Label>
            <SearchableSelect
              options={[{ value: '', label: 'All Trims' }, ...trimCodes]}
              value={filterTrim}
              onChange={setFilterTrim}
              placeholder="Select trim"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Filter by Status</Label>
            <SearchableSelect
              options={[{ value: '', label: 'All Statuses' }, ...STATUS_OPTIONS]}
              value={filterStatus}
              onChange={setFilterStatus}
              placeholder="Select status"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Filter by Digital Design</Label>
            <SearchableSelect
              options={[{ value: '', label: 'All Designs' }, ...digitalDesignCodes]}
              value={filterDigitalDesign}
              onChange={setFilterDigitalDesign}
              placeholder="Select digital design"
            />
          </div>
        </div>

        {/* Grid Area */}
        {filteredDesigns.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 text-lg">No designs found matching your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {filteredDesigns.map(design => (
              <div
                key={design.id}
                className="bg-white border-2 border-black rounded-lg shadow p-4 flex flex-col items-center justify-between hover:shadow-lg transition-shadow cursor-pointer design-card-printable"
                onClick={() => handleView(design)}
              >
                <h3 className="text-lg font-bold text-center mb-2">{design.article_code}</h3>
                <p className="text-xs text-gray-500 text-center mb-3">
                  {design.status.replace(/_/g, ' ')}
                </p>
                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                  {canEdit('fashion_design') && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(design);
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                  {canDelete('fashion_design') && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(design);
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </main>

      {/* Create/Edit Form */}
      <FashionDesignForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={handleFormSuccess}
        editingDesign={editingDesign}
      />

      {/* View Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader className="border-b-2 border-black pb-4">
            <div className="flex justify-between items-center">
              <DialogTitle className="text-xl font-bold tracking-tight uppercase">Design Details</DialogTitle>
              {viewDetails && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleDownloadPDF}
                  className="gap-2"
                >
                  <Download className="w-4 h-4" /> Download PDF
                </Button>
              )}
            </div>
          </DialogHeader>
          {viewDetails && (
            <div ref={pdfContentRef} className="space-y-1">
              {/* PAGE 1: Basic Info, Fabrics, Digital Design, Status, Trims */}
              <div style={{ pageBreakAfter: 'always' }}>
              {/* Basic Info with Barcode */}
              <div className="p-5 border-2 border-black" style={{ pageBreakInside: 'avoid' }}>
                <div className="flex justify-between items-start">
                  <div className="grid grid-cols-2 gap-6 flex-1">
                    <div>
                      <p className="text-xs font-semibold tracking-wider text-gray-500 mb-1">ARTICLE CODE</p>
                      <p className="text-sm font-bold">{viewDetails.article_code}</p>
                      {/* Tech Pack, LN-X Reports, and Costing Buttons - Hidden in PDF */}
                      <div className="flex gap-2 mt-2 no-pdf">
                        {viewDetails.tech_pack_pdf && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleTechPackClick}
                          >
                            <FileText className="w-3 h-3 mr-1" /> Tech Pack
                          </Button>
                        )}
                        {lnxReportData && lnxReportData.pdf_url && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleLnxReportClick}
                          >
                            <FileText className="w-3 h-3 mr-1" /> LN-X Report
                          </Button>
                        )}
                        {viewDetails.costing_approved && viewDetails.costing_data && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleCostingClick}
                          >
                            <DollarSign className="w-3 h-3 mr-1" /> Costing
                          </Button>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold tracking-wider text-gray-500 mb-1">CATEGORY</p>
                      <p className="text-sm font-bold">{viewDetails.category || '-'}</p>
                    </div>
                    {viewDetails.project_cycle > 0 && (
                      <div className="col-span-2">
                        <p className="text-xs font-bold tracking-wider text-red-600">PREVIOUS DATASET (CYCLE {viewDetails.project_cycle})</p>
                      </div>
                    )}
                  </div>
                  {/* Barcode in top right */}
                  {viewDetails.article_barcode && (
                    <div className="ml-6 flex-shrink-0">
                      <img src={viewDetails.article_barcode} alt="Article Barcode" style={{ maxWidth: '250px', height: 'auto' }} />
                    </div>
                  )}
                </div>
              </div>

              {/* Fabrics */}
              {viewDetails.fabrics && viewDetails.fabrics.length > 0 && (
                <div className="p-5 border-2 border-black" style={{ pageBreakInside: 'avoid' }}>
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">FABRICS</p>
                  <div className="space-y-3">
                    {viewDetails.fabrics.map((fabric, index) => (
                      <div key={index} className="p-4 border border-gray-300 bg-gray-50">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">CODE</p>
                            <p 
                              className="text-sm font-bold underline cursor-pointer hover:text-blue-600"
                              onClick={() => handleMasterDataClick(fabric.fabric_code, 'Fabric')}
                            >
                              {fabric.fabric_code}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">COLOR</p>
                            <p className="text-sm">{fabric.color_code} - {fabric.color_name}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">CONSUMPTION</p>
                            <p className="text-sm">{fabric.consumption} {parseFloat(fabric.consumption) > 1 ? 'Meters' : 'Meter'}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">COMPOSITION</p>
                            <p className="text-sm">{fabric.composition || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">WIDTH</p>
                            <p className="text-sm">{fabric.width}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">GSM</p>
                            <p className="text-sm">{fabric.gsm}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Digital Design */}
              {viewDetails.digital_design_code && (
                <div className="p-5 border-2 border-black" style={{ pageBreakInside: 'avoid' }}>
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">DIGITAL DESIGN</p>
                  <div className="p-4 border border-gray-300 bg-gray-50">
                    <div className="grid grid-cols-3 gap-6">
                      {/* Left side - Text info (2/3 width) */}
                      <div className="col-span-2 grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">CODE</p>
                          <p 
                            className="text-sm font-bold underline cursor-pointer hover:text-blue-600"
                            onClick={() => handleMasterDataClick(viewDetails.digital_design_code, 'DigitalDesign')}
                          >
                            {viewDetails.digital_design_code}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">COLORWAY</p>
                          <p className="text-sm">{viewDetails.digital_design_colorway || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">TYPE</p>
                          <p className="text-sm">{viewDetails.digital_design_type || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">CONSUMPTION</p>
                          <p className="text-sm">{viewDetails.digital_design_consumption ? `${viewDetails.digital_design_consumption} ${parseFloat(viewDetails.digital_design_consumption) > 1 ? 'Meters' : 'Meter'}` : '-'}</p>
                        </div>
                      </div>
                      
                      {/* Right side - Colorway Image (1/3 width) */}
                      <div className="col-span-1">
                        {viewDetails.digital_design_colorway_image ? (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-gray-500">COLORWAY IMAGE</p>
                            <div className="flex flex-col items-start gap-2">
                              <img 
                                src={viewDetails.digital_design_colorway_image} 
                                alt="Colorway" 
                                className="w-auto h-[120px] object-contain rounded cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => { setZoomImage(viewDetails.digital_design_colorway_image); setZoomImageOpen(true); }}
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                className="no-pdf"
                                onClick={async () => {
                                  try {
                                    const response = await fetch(viewDetails.digital_design_colorway_image);
                                    const blob = await response.blob();
                                    const blobUrl = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = blobUrl;
                                    link.download = `${viewDetails.digital_design_code}_colorway.png`;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(blobUrl);
                                  } catch (error) {
                                    console.error('Download failed:', error);
                                    toast.error('Failed to download image');
                                  }
                                }}
                              >
                                <Download className="w-3 h-3 mr-1" /> Download
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-gray-400">COLORWAY IMAGE</p>
                            <div className="flex items-start">
                              <p className="text-xs text-gray-400">No image</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  </div>
                </div>
              )}

              {/* Status */}
              <div className="p-5 border-2 border-black bg-black text-white" style={{ pageBreakInside: 'avoid' }}>
                <p className="text-xs font-semibold tracking-wider mb-4">STATUS</p>
                <div>
                  <p className="text-xs font-semibold text-gray-300 mb-1">CURRENT STATUS</p>
                  <p className="text-lg font-bold">{viewDetails.status.replace(/_/g, ' ')}</p>
                </div>
              </div>

              {/* Trims & Accessories - End of Page 1 */}
              {viewDetails.trims && viewDetails.trims.length > 0 && (
                <div className="p-5 border-2 border-black" style={{ pageBreakInside: 'avoid' }}>
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">TRIMS & ACCESSORIES</p>
                  <div className="space-y-2">
                    {viewDetails.trims.map((trim, index) => {
                      // Determine if it's Trims or Packaging based on code prefix
                      const masterType = trim.trim_code.startsWith('LN-Z') ? 'Trims' : 'Packaging';
                      return (
                        <div key={index} className="flex gap-4 items-center p-3 border border-gray-300">
                          <span 
                            className="text-sm font-bold w-32 underline cursor-pointer hover:text-blue-600"
                            onClick={() => handleMasterDataClick(trim.trim_code, masterType)}
                          >
                            {trim.trim_code}
                          </span>
                          <span className="flex-1 text-sm">{trim.color_code} - {trim.color_name}</span>
                          <span className="text-sm font-bold">QTY {trim.quantity}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              </div>

              {/* PAGE 2: Points of Measurement */}
              {viewDetails.poms && viewDetails.poms.length > 0 && (
                <div style={{ pageBreakAfter: 'always' }}>
                <div className="p-5 border-2 border-black" style={{ pageBreakInside: 'avoid' }}>
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">POINTS OF MEASUREMENT</p>
                  <div className="space-y-2">
                    {viewDetails.poms.map((pom, index) => (
                      <div key={index} className="flex gap-4 items-center p-3 border border-gray-300">
                        <span className="text-sm font-bold w-20">SIZE {pom.size}</span>
                        <span className="flex-1 text-sm">{pom.point_name}</span>
                        <span className="text-sm font-bold">{pom.measurement} {pom.uom}</span>
                      </div>
                    ))}
                  </div>
                </div>
                </div>
              )}

              {/* PAGE 3: Design Images in 2x2 Grid */}
              <div style={{ pageBreakAfter: 'auto' }}>
              {(viewDetails.inspiration_image_path || viewDetails.sketch_front_image_path || viewDetails.sketch_back_image_path || viewDetails.finished_good_image_path) && (
                <div className="p-5 border-2 border-black" style={{ pageBreakInside: 'avoid' }}>
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">DESIGN IMAGES</p>
                  <div className="grid grid-cols-2 gap-6">
                    {viewDetails.inspiration_image_path && (
                      <div className="space-y-2">
                        <p className="text-sm font-bold text-gray-700 mb-2">INSPIRATION</p>
                        <img 
                          src={`${API}/${viewDetails.inspiration_image_path}`} 
                          alt="Inspiration" 
                          className="w-full h-64 object-contain border-2 border-gray-300 cursor-pointer hover:opacity-80 transition-opacity" 
                          onClick={() => { setZoomImage(`${API}/${viewDetails.inspiration_image_path}`); setZoomImageOpen(true); }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full no-pdf"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `${API}/${viewDetails.inspiration_image_path}`;
                            link.download = `${viewDetails.article_code}_inspiration.png`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          <Download className="w-3 h-3 mr-1" /> Download
                        </Button>
                      </div>
                    )}
                    {viewDetails.sketch_front_image_path && (
                      <div className="space-y-2">
                        <p className="text-sm font-bold text-gray-700 mb-2">SKETCH FRONT</p>
                        <img 
                          src={`${API}/${viewDetails.sketch_front_image_path}`} 
                          alt="Sketch Front" 
                          className="w-full h-64 object-contain border-2 border-gray-300 cursor-pointer hover:opacity-80 transition-opacity" 
                          onClick={() => { setZoomImage(`${API}/${viewDetails.sketch_front_image_path}`); setZoomImageOpen(true); }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full no-pdf"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `${API}/${viewDetails.sketch_front_image_path}`;
                            link.download = `${viewDetails.article_code}_sketch_front.png`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          <Download className="w-3 h-3 mr-1" /> Download
                        </Button>
                      </div>
                    )}
                    {viewDetails.sketch_back_image_path && (
                      <div className="space-y-2">
                        <p className="text-sm font-bold text-gray-700 mb-2">SKETCH BACK</p>
                        <img 
                          src={`${API}/${viewDetails.sketch_back_image_path}`} 
                          alt="Sketch Back" 
                          className="w-full h-64 object-contain border-2 border-gray-300 cursor-pointer hover:opacity-80 transition-opacity" 
                          onClick={() => { setZoomImage(`${API}/${viewDetails.sketch_back_image_path}`); setZoomImageOpen(true); }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full no-pdf"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `${API}/${viewDetails.sketch_back_image_path}`;
                            link.download = `${viewDetails.article_code}_sketch_back.png`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          <Download className="w-3 h-3 mr-1" /> Download
                        </Button>
                      </div>
                    )}
                    {viewDetails.finished_good_image_path && (
                      <div className="space-y-2">
                        <p className="text-sm font-bold text-gray-700 mb-2">FINISHED GOOD IMAGE</p>
                        <img 
                          src={`${API}/${viewDetails.finished_good_image_path}`} 
                          alt="Finished Good" 
                          className="w-full h-64 object-contain border-2 border-gray-300 cursor-pointer hover:opacity-80 transition-opacity" 
                          onClick={() => { setZoomImage(`${API}/${viewDetails.finished_good_image_path}`); setZoomImageOpen(true); }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full no-pdf"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `${API}/${viewDetails.finished_good_image_path}`;
                            link.download = `${viewDetails.article_code}_finished_good.png`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          <Download className="w-3 h-3 mr-1" /> Download
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Comments - On Page 3 after images */}
              {viewDetails.comments && (
                <div className="p-5 border-2 border-black" style={{ pageBreakInside: 'avoid' }}>
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-3">COMMENTS</p>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{viewDetails.comments}</p>
                </div>
              )}
              </div>

              {/* Machine Operator Data - Compact */}
              {samplingTimeData && (
                <div className="p-3 border border-gray-300 bg-gray-50 rounded">
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-2">MACHINE OPERATOR DATA</p>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="p-2 bg-white border border-gray-300 rounded">
                      <p className="text-[10px] font-semibold text-gray-500 mb-1">SAMPLING TIME</p>
                      <p className="text-sm font-medium">
                        {Math.floor(samplingTimeData.total_time_seconds / 60)} min
                      </p>
                    </div>
                    <div className="p-2 bg-white border border-gray-300 rounded">
                      <p className="text-[10px] font-semibold text-gray-500 mb-1">EST. PROD. TIME</p>
                      <p className="text-sm font-medium">
                        {Math.floor(samplingTimeData.total_time_seconds * 0.65 / 60)} min
                      </p>
                    </div>
                    <div className="p-2 bg-white border border-gray-300 rounded">
                      <p className="text-[10px] font-semibold text-gray-500 mb-1">SAMPLER</p>
                      <p className="text-sm font-medium">{samplingTimeData.sampler_name}</p>
                    </div>
                    <div className="p-2 bg-white border border-gray-300 rounded">
                      <p className="text-[10px] font-semibold text-gray-500 mb-1">COMPLETION DATE</p>
                      <p className="text-sm font-medium">
                        {samplingTimeData.end_time ? new Date(samplingTimeData.end_time).toLocaleDateString() : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {/* Delete Authorization Dialog */}
      <AuthorizationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onAuthorized={confirmDelete}
        title="Delete Design"
        message={designToDelete ? `Enter authorization code to delete design ${designToDelete.design_code}` : 'Enter authorization code to proceed'}
      />

      {/* Tech Pack Authorization Dialog */}
      <AuthorizationDialog
        open={techPackAuthDialogOpen}
        onOpenChange={setTechPackAuthDialogOpen}
        onAuthorized={handleTechPackAuthSuccess}
        title="Tech Pack Access"
        message="Enter authorization code to view Tech Pack"
        correctCode="override"
      />

      {/* Costing Authorization Dialog */}
      <AuthorizationDialog
        open={costingAuthDialogOpen}
        onOpenChange={setCostingAuthDialogOpen}
        onAuthorized={handleCostingAuthSuccess}
        title="Costing Access"
        message="Enter authorization code to view Costing"
        correctCode="override"
      />

      {/* Sampling Round Dialog */}
      <Dialog open={samplingRoundDialogOpen} onOpenChange={setSamplingRoundDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader className="border-b-2 border-black pb-4">
            <DialogTitle className="text-xl font-bold tracking-tight uppercase">Create Sampling Round</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Round Number</Label>
              <Input
                type="number"
                value={samplingRoundNumber}
                onChange={(e) => setSamplingRoundNumber(e.target.value)}
                placeholder="Enter round number"
                className="border-2 border-black"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-3 block">Select Articles for this Round</Label>
              <div className="border-2 border-black rounded p-4 max-h-96 overflow-y-auto">
                {designs.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No articles available</p>
                ) : (
                  <div className="space-y-2">
                    {/* Select All / Deselect All */}
                    <div className="flex items-center gap-2 pb-3 border-b-2 border-gray-200 mb-3">
                      <input
                        type="checkbox"
                        id="select-all"
                        checked={selectedArticles.length === designs.length && designs.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedArticles(designs.map(d => d.id));
                          } else {
                            setSelectedArticles([]);
                          }
                        }}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <label htmlFor="select-all" className="text-sm font-bold cursor-pointer">
                        Select All ({designs.length} articles)
                      </label>
                    </div>
                    
                    {/* Individual article checkboxes */}
                    {designs.map((design) => (
                      <div key={design.id} className="flex items-start gap-3 p-3 border border-gray-300 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          id={`article-${design.id}`}
                          checked={selectedArticles.includes(design.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedArticles([...selectedArticles, design.id]);
                            } else {
                              setSelectedArticles(selectedArticles.filter(id => id !== design.id));
                            }
                          }}
                          className="w-4 h-4 mt-1 cursor-pointer flex-shrink-0"
                        />
                        <label htmlFor={`article-${design.id}`} className="flex-1 cursor-pointer">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-bold">{design.article_code}</p>
                              {design.category && (
                                <p className="text-xs text-gray-600">{design.category}</p>
                              )}
                            </div>
                            <span className="text-xs px-2 py-1 bg-gray-200 rounded">
                              {design.status.replace(/_/g, ' ')}
                            </span>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-600 mt-2">
                {selectedArticles.length} article(s) selected
              </p>
            </div>
            
            <div className="flex gap-3 pt-4 border-t-2 border-gray-200">
              <Button 
                onClick={() => {
                  setSamplingRoundDialogOpen(false);
                  setSamplingRoundNumber('');
                  setSelectedArticles([]);
                }} 
                variant="outline"
                className="flex-1 border-2 border-black"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateSamplingRound}
                className="flex-1 bg-black hover:bg-gray-800"
                disabled={selectedArticles.length === 0}
              >
                Create Round ({selectedArticles.length} articles)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dataset Selection Dialog */}
      <Dialog open={datasetSelectionOpen} onOpenChange={setDatasetSelectionOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader className="border-b-2 border-black pb-4">
            <DialogTitle className="text-xl font-bold tracking-tight uppercase">Generate Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <p className="text-sm font-medium">Select which dataset to include in the report:</p>
            
            {/* Option 1: All Articles */}
            <div 
              className={`p-4 border-2 rounded cursor-pointer transition-all ${
                reportOption === 'all' ? 'border-black bg-gray-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              onClick={() => setReportOption('all')}
            >
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  name="report-option"
                  checked={reportOption === 'all'}
                  onChange={() => setReportOption('all')}
                  className="w-4 h-4 cursor-pointer"
                />
                <div className="flex-1">
                  <p className="text-sm font-bold">All Articles</p>
                  <p className="text-xs text-gray-600">Include all articles across all datasets</p>
                </div>
              </div>
            </div>

            {/* Option 2: Specific Dataset Rounds */}
            <div 
              className={`p-4 border-2 rounded cursor-pointer transition-all ${
                reportOption === 'specific' ? 'border-black bg-gray-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              onClick={() => setReportOption('specific')}
            >
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  name="report-option"
                  checked={reportOption === 'specific'}
                  onChange={() => setReportOption('specific')}
                  className="w-4 h-4 cursor-pointer flex-shrink-0"
                />
                <div className="flex-1">
                  <p className="text-sm font-bold mb-2">Specific Dataset Round(s)</p>
                  <p className="text-xs text-gray-600 mb-3">Enter dataset round numbers</p>
                  {reportOption === 'specific' && (
                    <Input
                      type="text"
                      value={specificRoundNumbers}
                      onChange={(e) => setSpecificRoundNumbers(e.target.value)}
                      placeholder="Enter round numbers"
                      className="border-2 border-black"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  <p className="text-xs text-gray-500 mt-1">Enter multiple numbers separated by commas</p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 pt-4 border-t-2 border-gray-200">
              <Button 
                onClick={() => {
                  setDatasetSelectionOpen(false);
                  setReportOption('all');
                  setSpecificRoundNumbers('');
                }} 
                variant="outline"
                className="flex-1 border-2 border-black"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDatasetSelection}
                className="flex-1 bg-black hover:bg-gray-800"
              >
                Generate Report
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader className="border-b-2 border-black pb-4">
            <DialogTitle className="text-xl font-bold tracking-tight uppercase">Design HQ Report</DialogTitle>
          </DialogHeader>
          
          {reportData && (
            <div className="space-y-1">
              {/* Report Header */}
              <div className="p-5 border-2 border-black">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs font-semibold tracking-wider text-gray-500">GENERATED ON</p>
                    <p className="text-sm font-bold">{reportData.generatedAt}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold tracking-wider text-gray-500">DATASET</p>
                    <p className="text-sm font-bold">{reportData.datasetLabel}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold tracking-wider text-gray-500">TOTAL DESIGNS</p>
                    <p className="text-2xl font-bold">{reportData.total}</p>
                  </div>
                </div>
              </div>

              {/* Status Distribution Pie Chart */}
              <div className="p-5 border-2 border-black">
                <h3 className="text-xs font-semibold tracking-wider text-gray-500 mb-6">STATUS DISTRIBUTION</h3>
                <div className="flex justify-center mb-6">
                  <svg viewBox="0 0 200 200" className="w-80 h-80">
                    {(() => {
                      let currentAngle = 0;
                      const colors = ['#000000', '#4B5563', '#9CA3AF', '#D1D5DB', '#6B7280', '#374151', '#1F2937', '#111827'];
                      return reportData.statusData.map((item, idx) => {
                        const percentage = (item.count / reportData.total);
                        const angle = percentage * 360;
                        const startAngle = currentAngle;
                        const endAngle = currentAngle + angle;
                        currentAngle = endAngle;
                        
                        const startRad = (startAngle - 90) * (Math.PI / 180);
                        const endRad = (endAngle - 90) * (Math.PI / 180);
                        const x1 = 100 + 80 * Math.cos(startRad);
                        const y1 = 100 + 80 * Math.sin(startRad);
                        const x2 = 100 + 80 * Math.cos(endRad);
                        const y2 = 100 + 80 * Math.sin(endRad);
                        const largeArc = angle > 180 ? 1 : 0;
                        
                        return (
                          <g key={idx}>
                            <path
                              d={`M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArc} 1 ${x2} ${y2} Z`}
                              fill={colors[idx % colors.length]}
                              stroke="white"
                              strokeWidth="3"
                            />
                          </g>
                        );
                      });
                    })()}
                  </svg>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {reportData.statusData.map((item, idx) => {
                    const colors = ['#000000', '#4B5563', '#9CA3AF', '#D1D5DB', '#6B7280', '#374151', '#1F2937', '#111827'];
                    const percentage = ((item.count / reportData.total) * 100).toFixed(1);
                    return (
                      <div key={idx} className="flex items-center gap-3 p-3 border border-gray-300">
                        <div className="w-6 h-6 border border-gray-400" style={{ backgroundColor: colors[idx % colors.length] }}></div>
                        <div className="flex-1">
                          <p className="text-sm font-bold">{item.status}</p>
                          <p className="text-xs text-gray-500">{item.count} ({percentage}%)</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Category Distribution Pie Chart */}
              <div className="p-5 border-2 border-black">
                <h3 className="text-xs font-semibold tracking-wider text-gray-500 mb-6">CATEGORY DISTRIBUTION</h3>
                <div className="flex justify-center mb-6">
                  <svg viewBox="0 0 200 200" className="w-80 h-80">
                    {(() => {
                      let currentAngle = 0;
                      const colors = ['#000000', '#4B5563', '#9CA3AF', '#6B7280', '#374151', '#1F2937'];
                      const categoryEntries = Object.entries(reportData.categoryData);
                      return categoryEntries.map(([category, count], idx) => {
                        const percentage = count / reportData.total;
                        const angle = percentage * 360;
                        const startAngle = currentAngle;
                        const endAngle = currentAngle + angle;
                        currentAngle = endAngle;
                        
                        const startRad = (startAngle - 90) * (Math.PI / 180);
                        const endRad = (endAngle - 90) * (Math.PI / 180);
                        const x1 = 100 + 80 * Math.cos(startRad);
                        const y1 = 100 + 80 * Math.sin(startRad);
                        const x2 = 100 + 80 * Math.cos(endRad);
                        const y2 = 100 + 80 * Math.sin(endRad);
                        const largeArc = angle > 180 ? 1 : 0;
                        
                        return (
                          <g key={idx}>
                            <path
                              d={`M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArc} 1 ${x2} ${y2} Z`}
                              fill={colors[idx % colors.length]}
                              stroke="white"
                              strokeWidth="3"
                            />
                          </g>
                        );
                      });
                    })()}
                  </svg>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(reportData.categoryData).map(([category, count], idx) => {
                    const colors = ['#000000', '#4B5563', '#9CA3AF', '#6B7280', '#374151', '#1F2937'];
                    const percentage = ((count / reportData.total) * 100).toFixed(1);
                    return (
                      <div key={idx} className="flex items-center gap-3 p-3 border border-gray-300">
                        <div className="w-6 h-6 border border-gray-400" style={{ backgroundColor: colors[idx % colors.length] }}></div>
                        <div className="flex-1">
                          <p className="text-sm font-bold">{category}</p>
                          <p className="text-xs text-gray-500">{count} ({percentage}%)</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Master Data Details Popup */}
      <Dialog open={masterDataOpen} onOpenChange={setMasterDataOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{masterDataCode || 'Master Data Details'}</DialogTitle>
          </DialogHeader>
          {masterDataLoading ? (
            <div className="py-8 text-center">
              <p className="text-gray-500">Loading...</p>
            </div>
          ) : masterDataDetails ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                <div>
                  <p className="text-xs font-semibold text-gray-500">CODE</p>
                  <p className="text-sm font-bold">{masterDataDetails.ln_code || masterDataDetails.ln_z_code || masterDataDetails.ln_y_code || masterDataDetails.ln_t_code}</p>
                </div>
                
                {/* Show PRICE for all codes (LN, LN-Z, LN-Y, LN-T) */}
                {(masterDataDetails.colors && masterDataDetails.colors.length > 0) || masterDataDetails.price_per_meter ? (
                  <div>
                    <p className="text-xs font-semibold text-gray-500">PRICE</p>
                    <p className="text-sm font-bold">
                      {masterDataDetails.price_per_meter 
                        ? `$${Number(masterDataDetails.price_per_meter).toFixed(2)}/Meter`
                        : masterDataDetails.colors && masterDataDetails.colors[0]?.price_per_meter 
                        ? `$${Number(masterDataDetails.colors[0].price_per_meter).toFixed(2)}/Meter`
                        : masterDataDetails.colors && masterDataDetails.colors[0]?.price 
                        ? `$${Number(masterDataDetails.colors[0].price).toFixed(2)}`
                        : '-'}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center">
              <p className="text-gray-500">No data available</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Costing Dialog */}
      <Dialog open={showCostingDialog} onOpenChange={setShowCostingDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase border-b-2 border-black pb-2">
              PRE-PRODUCTION COSTING - {viewDetails?.article_code}
            </DialogTitle>
          </DialogHeader>
          {viewDetails?.costing_data && (
            <div className="space-y-4 mt-4">
              {/* Financial Document Style */}
              <div className="bg-white border-4 border-black">
                <div className="bg-black text-white p-4">
                  <h3 className="text-lg font-bold uppercase tracking-wider">PRE-PRODUCTION COSTING STATEMENT</h3>
                </div>
                
                <div className="p-6">
                  {/* Cost Breakdown Table */}
                  <table className="w-full border-2 border-black">
                    <thead>
                      <tr className="border-b-2 border-black bg-gray-100">
                        <th className="text-left p-4 border-r-2 border-black font-bold uppercase text-sm">COST CATEGORY</th>
                        <th className="text-right p-4 font-bold uppercase text-sm">AMOUNT (USD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-black">
                        <td className="p-4 border-r-2 border-black font-medium">Fabrics Total</td>
                        <td className="p-4 text-right font-mono">${viewDetails.costing_data.fabricsTotal?.toFixed(2) || '0.00'}</td>
                      </tr>
                      {viewDetails.costing_data.digitalDesignTotal > 0 && (
                        <tr className="border-b border-black">
                          <td className="p-4 border-r-2 border-black font-medium">Digital Design Total</td>
                          <td className="p-4 text-right font-mono">${viewDetails.costing_data.digitalDesignTotal?.toFixed(2) || '0.00'}</td>
                        </tr>
                      )}
                      <tr className="border-b border-black">
                        <td className="p-4 border-r-2 border-black font-medium">Trims & Accessories Total</td>
                        <td className="p-4 text-right font-mono">${viewDetails.costing_data.trimsTotal?.toFixed(2) || '0.00'}</td>
                      </tr>
                      <tr className="border-b border-black">
                        <td className="p-4 border-r-2 border-black font-medium">CMT (Cut, Make, Trim) Total</td>
                        <td className="p-4 text-right font-mono">${viewDetails.costing_data.cmtTotal?.toFixed(2) || '0.00'}</td>
                      </tr>
                      {viewDetails.costing_data.otherCostsTotal > 0 && (
                        <tr className="border-b border-black">
                          <td className="p-4 border-r-2 border-black font-medium">Other Costs Total</td>
                          <td className="p-4 text-right font-mono">${viewDetails.costing_data.otherCostsTotal?.toFixed(2) || '0.00'}</td>
                        </tr>
                      )}
                      <tr className="border-t-4 border-black bg-gray-100">
                        <td className="p-4 border-r-2 border-black font-bold uppercase">SUBTOTAL (PRE-PRODUCTION COSTING)</td>
                        <td className="p-4 text-right font-bold font-mono">${viewDetails.costing_data.preProductionCosting?.toFixed(2) || '0.00'}</td>
                      </tr>
                      <tr className="border-t-2 border-black">
                        <td className="p-4 border-r-2 border-black font-medium">Margin of Error (15%)</td>
                        <td className="p-4 text-right font-mono">${((viewDetails.costing_data.preProductionCosting || 0) * 0.15).toFixed(2)}</td>
                      </tr>
                      <tr className="border-t-4 border-double border-black bg-black text-white">
                        <td className="p-4 border-r-2 border-white font-bold uppercase">TOTAL WITH MARGIN</td>
                        <td className="p-4 text-right font-bold font-mono">${viewDetails.costing_data.withMargin?.toFixed(2) || '0.00'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tech Pack PDF Viewer */}
      <Dialog open={showTechPackPdf} onOpenChange={setShowTechPackPdf}>
        <DialogContent className="max-w-screen max-h-screen w-screen h-screen p-0 m-0 rounded-none border-0">
          <DialogHeader className="absolute top-0 left-0 right-0 z-10 px-6 py-3 bg-white border-b flex flex-row items-center justify-between">
            <DialogTitle>Tech Pack PDF - {viewDetails?.article_code}</DialogTitle>
            <button 
              onClick={() => setShowTechPackPdf(false)}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </DialogHeader>
          {viewDetails?.tech_pack_pdf && (
            <iframe
              src={viewDetails.tech_pack_pdf}
              className="w-full h-full border-0"
              title="Tech Pack PDF"
              style={{ marginTop: '52px' }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* LN-X Report PDF Viewer */}
      <Dialog open={showLnxReportPdf} onOpenChange={setShowLnxReportPdf}>
        <DialogContent className="max-w-screen max-h-screen w-screen h-screen p-0 m-0 rounded-none border-0">
          <DialogHeader className="absolute top-0 left-0 right-0 z-10 px-6 py-3 bg-white border-b flex flex-row items-center justify-between">
            <DialogTitle>LN-X Report PDF - {viewDetails?.article_code}</DialogTitle>
            <button 
              onClick={() => setShowLnxReportPdf(false)}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </DialogHeader>
          {lnxReportData?.pdf_url && (
            <iframe
              src={`${BACKEND_URL}${lnxReportData.pdf_url}`}
              className="w-full h-full border-0"
              title="LN-X Report PDF"
              style={{ marginTop: '52px' }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Image Zoom Dialog */}
      <Dialog open={zoomImageOpen} onOpenChange={setZoomImageOpen}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Image Preview</DialogTitle>
          </DialogHeader>
          {zoomImage && (
            <div className="flex justify-center">
              <img src={zoomImage} alt="Zoomed" className="max-w-full h-auto" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
